/**
 * @documental
 * @export Placeholder
 * @name
 * Button.Placeholder
 */
import { FC, PropsWithChildren } from "react";
import Skeleton from "react-loading-skeleton";

import { BaseProps } from "capitalrx-components-2.0/lib/BaseProps";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Semibold } from "capitalrx-components-2.0/lib/typography";
import { getHtmlPropsFromBaseProps } from "capitalrx-components-2.0/lib/utilities/getHtmlPropsFromBaseProps";
import classNames from "classnames";
import styled from "styled-components";

import { ButtonAppearance } from "./ButtonAppearances";

import styles from "./styles.module.scss";

/**
 * The `Placeholder` component properties.
 */
export interface PlaceholderProps extends BaseProps {
  /**
   * Indicates whether the placeholder should reserve space for the icon.
   */
  readonly icon?: boolean;

  /**
   * The button appearance.
   */
  readonly appearance: ButtonAppearance;
}

/**
 * The button with the appearance styling applied.
 */
export const StyledPlaceholderSpan = styled.span<{ appearance: ButtonAppearance; iconOnly: boolean }>`
  display: inline-block;
  max-height: content-height;
  letter-spacing: ${(props) => props.appearance.label.letterSpacing}px;
  white-space: nowrap;
  padding: ${(props) => {
    const { vertical } = props.appearance.padding;
    let { horizontal } = props.appearance.padding;
    if (props.iconOnly) horizontal = vertical;

    return `calc(${vertical / 16}rem + 1px) calc(${horizontal / 16}rem + 1px)`;
  }};
  font-size: ${(props) => props.appearance.label.fontSize / 16}rem;
  line-height: ${(props) => props.appearance.label.lineHeight / 16}rem;
  border-radius: 0.25rem;
  overflow: hidden;
`;

/**
 * A loading placeholder for the `Button` component.
 */
export const Placeholder: FC<PropsWithChildren<PlaceholderProps>> = (props) => {
  const { children, icon = false, appearance } = props;

  const iconOnly = icon && !children;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.ButtonPlaceholder,
      "button-placeholder",
      {
        "icon-only": iconOnly,
      },
    ],
    testIdPrefix: "button-placeholder",
  });

  return (
    <StyledPlaceholderSpan {...baseHtmlProps} appearance={appearance} iconOnly={iconOnly} role="button" aria-busy>
      <Skeleton containerClassName={styles.ButtonSkeleton} />
      <Shelf spacing="small">
        {icon && <span className={classNames(styles.IconPlaceholder, "icon-placeholder")} />}
        {!iconOnly && <Semibold>{children ?? <>&nbsp;</>}</Semibold>}
      </Shelf>
    </StyledPlaceholderSpan>
  );
};
